import { useState, useMemo } from 'react';
import { X, History, ChevronDown } from 'lucide-react';
import { useSpecHistory, useSpecDiff } from '../../hooks/useSpecifications';
import Spinner from '../ui/Spinner';
import { DiffField } from './SpecDiffView';

export default function SpecDiffModal({
  projectId,
  specId,
  currentVersion,
  onClose,
}: {
  projectId: string;
  specId: string;
  currentVersion: number;
  onClose: () => void;
}) {
  const { data: history, isLoading: historyLoading } = useSpecHistory(projectId, specId);

  const [v1, setV1] = useState(0);
  const [v2, setV2] = useState(0);

  // 기본 선택: 이전 버전 vs 현재 버전
  useMemo(() => {
    if (history && history.length > 0 && v1 === 0 && v2 === 0) {
      setV1(history[0].version); // 최근 이전 버전
      setV2(currentVersion); // 현재 버전
    }
  }, [history, currentVersion, v1, v2]);

  const { data: diffResult, isLoading: diffLoading } = useSpecDiff(
    projectId,
    specId,
    v1,
    v2,
  );

  const changedDiffs = useMemo(
    () => diffResult?.diffs.filter((d) => d.type !== 'unchanged') ?? [],
    [diffResult],
  );

  // 버전 목록 (이력 + 현재 버전)
  const versionOptions = useMemo(() => {
    const options: { value: number; label: string }[] = [];
    if (history) {
      for (const h of history) {
        const date = new Date(h.changedAt).toLocaleString('ko-KR', {
          month: 'short',
          day: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
        options.push({ value: h.version, label: `v${h.version} — ${h.changer.name} (${date})` });
      }
    }
    options.unshift({ value: currentVersion, label: `v${currentVersion} (현재)` });
    return options;
  }, [history, currentVersion]);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[85vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-200">
          <div className="flex items-center gap-2">
            <History className="size-5 text-primary-600" />
            <h3 className="text-lg font-semibold text-gray-900">변경 이력 비교</h3>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 transition-colors">
            <X className="size-5 text-gray-500" />
          </button>
        </div>

        {/* Version selectors */}
        <div className="px-6 py-3 bg-gray-50 border-b border-gray-200 flex items-center gap-4">
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">이전 버전</label>
            <div className="relative">
              <select
                value={v1}
                onChange={(e) => setV1(Number(e.target.value))}
                className="appearance-none pl-3 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
              >
                <option value={0}>선택...</option>
                {versionOptions.map((o) => (
                  <option key={`v1-${o.value}`} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
          <span className="text-gray-400">→</span>
          <div className="flex items-center gap-2">
            <label className="text-sm font-medium text-gray-600">이후 버전</label>
            <div className="relative">
              <select
                value={v2}
                onChange={(e) => setV2(Number(e.target.value))}
                className="appearance-none pl-3 pr-8 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 outline-none bg-white"
              >
                <option value={0}>선택...</option>
                {versionOptions.map((o) => (
                  <option key={`v2-${o.value}`} value={o.value}>
                    {o.label}
                  </option>
                ))}
              </select>
              <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 size-4 text-gray-400 pointer-events-none" />
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto px-6 py-4">
          {historyLoading ? (
            <div className="flex items-center justify-center h-40">
              <Spinner className="size-6" />
            </div>
          ) : !history || history.length === 0 ? (
            <div className="flex flex-col items-center justify-center h-40 text-gray-400">
              <History className="size-10 mb-2" />
              <p className="text-sm">변경 이력이 없습니다.</p>
              <p className="text-sm mt-1">명세를 수정하면 이력이 기록됩니다.</p>
            </div>
          ) : v1 === 0 || v2 === 0 || v1 === v2 ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              비교할 두 버전을 선택해주세요.
            </div>
          ) : diffLoading ? (
            <div className="flex items-center justify-center h-40">
              <Spinner className="size-6" />
            </div>
          ) : changedDiffs.length === 0 ? (
            <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
              두 버전 간 변경 사항이 없습니다.
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-sm text-gray-500">
                {changedDiffs.length}개 필드가 변경되었습니다.
              </p>
              {changedDiffs.map((diff) => (
                <DiffField key={diff.field} diff={diff} />
              ))}
            </div>
          )}
        </div>

        {/* History list */}
        {history && history.length > 0 && (
          <div className="px-6 py-3 border-t border-gray-200 bg-gray-50">
            <details>
              <summary className="text-sm font-medium text-gray-600 cursor-pointer hover:text-gray-800">
                전체 변경 이력 ({history.length}건)
              </summary>
              <div className="mt-2 space-y-1 max-h-40 overflow-y-auto">
                {history.map((h) => (
                  <div
                    key={h.id}
                    className="flex items-center justify-between text-sm text-gray-500 py-1"
                  >
                    <span>
                      <span className="font-medium text-gray-700">v{h.version}</span>
                      {' — '}
                      {h.changer.name}
                      {h.changeNote && (
                        <span className="text-gray-400 ml-1">({h.changeNote})</span>
                      )}
                    </span>
                    <span>
                      {new Date(h.changedAt).toLocaleString('ko-KR', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </span>
                  </div>
                ))}
              </div>
            </details>
          </div>
        )}
      </div>
    </div>
  );
}
